import type { AssistantEngine } from "../interfaces/AssistantEngine.js";
import type { OfflineKnowledgeProvider } from "../interfaces/OfflineKnowledgeProvider.js";
import { MountainSafetyRules } from "../safety/MountainSafetyRules.js";
import type { AssistantResponse } from "../types/AssistantResponse.js";
import type { MountainContext } from "../types/MountainContext.js";
import { ContextValidator } from "../validation/ContextValidator.js";
import { ResponseBuilder } from "./ResponseBuilder.js";

export interface MountainAssistantCoreDependencies {
  readonly validator?: ContextValidator;
  readonly safetyRules?: MountainSafetyRules;
  readonly offlineKnowledge?: OfflineKnowledgeProvider;
  readonly assistantEngine?: AssistantEngine;
}

export class MountainAssistantCore {
  private readonly validator: ContextValidator;
  private readonly safetyRules: MountainSafetyRules;
  private readonly offlineKnowledge: OfflineKnowledgeProvider | undefined;
  private readonly assistantEngine: AssistantEngine | undefined;

  constructor(dependencies: MountainAssistantCoreDependencies = {}) {
    this.validator = dependencies.validator ?? new ContextValidator();
    this.safetyRules = dependencies.safetyRules ?? new MountainSafetyRules();
    this.offlineKnowledge = dependencies.offlineKnowledge;
    this.assistantEngine = dependencies.assistantEngine;
  }

  async evaluate(context: MountainContext): Promise<AssistantResponse> {
    const validation = this.validator.validate(context);
    if (!validation.valid) {
      return ResponseBuilder.build({
        message: "Informacion no disponible: los datos recibidos no son suficientes o contienen valores invalidos.",
        severity: "INSUFFICIENT_DATA",
        confidence: 1,
        dataUsed: [],
        dataTimestamp: context.timestamp,
        offline: context.offline,
        recommendedAction: "Verifica los datos antes de continuar con una recomendacion basada en Mountain AI.",
        limitations: validation.issues,
        source: "SYSTEM",
      });
    }

    const safetyResponse = this.safetyRules.evaluate(context);
    if (safetyResponse) return safetyResponse;

    if (!context.gps && !context.route && !context.weather && !(context.alerts?.length)) {
      return ResponseBuilder.build({
        message: "Informacion no disponible: no hay datos contextuales suficientes para generar una evaluacion.",
        severity: "INSUFFICIENT_DATA",
        confidence: 1,
        dataUsed: [],
        dataTimestamp: context.timestamp,
        offline: context.offline,
        recommendedAction: "Obtenga datos contextuales validos antes de solicitar una evaluacion.",
        limitations: ["No se recibio GPS, progreso de ruta, clima ni alertas."],
        source: "SYSTEM",
      });
    }

    if (context.offline && this.offlineKnowledge) {
      const offlineResponse = this.offlineKnowledge.evaluate(context);
      if (offlineResponse) return offlineResponse;
    }

    if (this.assistantEngine) {
      const assistantResponse = await this.assistantEngine.respond(context);
      if (assistantResponse) return assistantResponse;
    }

    return ResponseBuilder.build({
      message: "Informacion contextual disponible. No se detectaron alertas deterministas en esta evaluacion.",
      severity: context.offline ? "OFFLINE_LIMITED" : "INFORMATION",
      confidence: 0.8,
      dataUsed: [
        ...(context.gps ? ["gps"] : []),
        ...(context.route ? ["route"] : []),
        ...(context.weather ? ["weather"] : []),
        ...(context.alerts?.length ? ["alerts"] : []),
      ],
      dataTimestamp: context.timestamp,
      offline: context.offline,
      recommendedAction: "Usa esta informacion solo como apoyo y confirma las condiciones reales antes de decidir.",
      limitations: context.offline ? ["Funcionamiento offline limitado."] : [],
      source: "SYSTEM",
    });
  }
}
